import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { getColorfulGameBGByIndex } from '../../../../constants/Games/gameBackground';
import { SVGIconType } from '../../../../constants/svgIcon';
import { GameController } from '../../../../models/GameControllerStore';
import {
  LoadingSpinner,
  Skeleton,
  SvgIcon,
  TTSPlayer,
  TTSPlayerRef,
} from '../../../Molecules';
import {
  GameHud,
  GameHudProps,
  GameQuestionPanel,
  HandwritingGameMainGroup,
  HandwritingGameMainGroupProps,
  HandwritingGameMode,
  HandwritingGameQuestionPanel,
  MemoryGameMainGroup,
  MemoryGameMainGroupProps,
  TTSQuestionPanel,
  useHandwritingGameStore,
  useMemoryGameStore,
} from '../../../Organisms';

import styles from './GameplayTemplate.module.css';

type GameplayTemplateProps = {
  gameBGIndex: number | undefined;
  questionTtsFileId: string | undefined;
  gameController: GameController;
  gameHudProps: GameHudProps;
  memoryGameMainGroupProps?: MemoryGameMainGroupProps;
  handwritingGameMainGroupProps?: HandwritingGameMainGroupProps;
  isLoading: boolean;
};

const GameplayTemplate = ({
  gameBGIndex,
  questionTtsFileId,
  gameController,
  gameHudProps,
  memoryGameMainGroupProps,
  handwritingGameMainGroupProps,
  isLoading,
}: GameplayTemplateProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const TTsPlayerRef = useRef<TTSPlayerRef>();

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const gameBG =
    gameBGIndex !== undefined
      ? getColorfulGameBGByIndex(gameBGIndex)
      : undefined;

  const {
    modifiedAnswer,
    blankUnits,
    currentBlankIndex,
    preFilledIndexes,
    blankInfos,
    mode,
    useColor,
  } = useMemoryGameStore();

  const {
    currentBlankIndex: writingCurrentBlankIndex,
    blankInfos: writingBlankInfos,
    blankUnits: writingBlankUnits,
  } = useHandwritingGameStore();

  // FIXME: this is for instruction panel to occupy the remaining height, may try flex: 1 instead?
  const viewportHeight = window.innerHeight;
  const mainGroupHeight =
    document.querySelector(`.${styles.mainGroupContainer}`)?.clientHeight || 0;
  const adsHeight =
    document.querySelector(`.${styles.adsContainer}`)?.clientHeight || 0;
  const offsetValue = handwritingGameMainGroupProps === undefined ? 60 : 80; // padding or gap space, adjust if needed
  const remainingHeight =
    viewportHeight - mainGroupHeight - adsHeight - offsetValue;
  const infoContainer = document.querySelector(
    `.${styles.infoContainer}`,
  ) as HTMLElement;
  if (infoContainer) {
    infoContainer.style.maxHeight = `${remainingHeight}px`;
  }

  const questionContainerHeight =
    document.querySelector(`.${styles.questionContainer}`)?.clientHeight || 0;

  const questionPanelHeight = window.innerHeight * 0.1; // this depends on HandwritingGameQuestionPanel css min-height

  const gameHudHeight =
    document.querySelector(`.game-hud-container`)?.clientHeight || 0;

  const infoContainerMinHeight =
    gameHudHeight + questionPanelHeight + questionContainerHeight;

  if (infoContainer) {
    infoContainer.style.minHeight = `${infoContainerMinHeight}px`;
  }

  // console.log(
  //   remainingHeight,
  //   viewportHeight,
  //   mainGroupHeight,
  //   adsHeight,
  //   offsetValue,
  // );

  // console.log(
  //   gameHudHeight,
  //   questionPanelHeight,
  //   questionContainerHeight,
  //   infoContainerMinHeight,
  // );

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'auto';
    document.body.style.scrollBehavior = 'auto';
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  const wordTtsAudio = memoryGameMainGroupProps
    ? blankUnits?.[currentBlankIndex]?.wordTtsAudio
    : writingBlankUnits?.[writingCurrentBlankIndex]?.wordTtsAudio;

  const ttsFileId = questionTtsFileId ?? wordTtsAudio;
  const audioSrc = gameController.getAudio(ttsFileId);

  return (
    <div
      className={styles.backgroundImage}
      style={
        gameBG
          ? {
              backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)),
              url(${gameBG})`,
            }
          : undefined
      }
    >
      <div className={styles.container}>
        {isLoading && <LoadingSpinner loading={isLoading} />}

        <div className={styles.infoContainer}>
          {gameHudProps.instruction && (
            <div
              className={`${styles.questionContainer} ${
                memoryGameMainGroupProps !== undefined
                  ? styles.memoryQuestionBg
                  : styles.handwritingQuestionBg
              }`}
            >
              {audioSrc ? (
                <TTSPlayer
                  ref={TTsPlayerRef}
                  audioSrc={audioSrc}
                  onClickPlay={() => {
                    gameController.stopAudio();
                  }}
                />
              ) : null}
              <div className={styles.questionText}>
                {gameHudProps.instruction}
              </div>
              <div className={styles.expandQuestionButton} onClick={openModal}>
                <SvgIcon
                  name={SVGIconType.Arrow}
                  width={20}
                  height={20}
                  color="#808080"
                />
              </div>
            </div>
          )}

          {modifiedAnswer && memoryGameMainGroupProps && (
            <GameQuestionPanel
              answer={modifiedAnswer}
              currentBlankIndex={currentBlankIndex}
              spellingPreFilledIndexes={preFilledIndexes}
              blankInfos={blankInfos}
              mode={mode}
              useColor={useColor}
              isLearner={false}
              isGameStart={false}
            />
          )}

          {handwritingGameMainGroupProps && (
            <HandwritingGameQuestionPanel
              answer={handwritingGameMainGroupProps.answer}
              currentBlankIndex={writingCurrentBlankIndex}
              blankInfos={writingBlankInfos}
              mode={HandwritingGameMode.QuickHandwriting}
              isLearner={false}
              isGameStart={false}
            />
          )}

          <GameHud
            {...{
              ...gameHudProps,
              onClickExpandInstruction: gameHudProps.instruction
                ? openModal
                : undefined,
            }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            // flexGrow: 1,
            width: '100%',
            marginTop: 'auto',
            zIndex: '5',
          }}
          className={styles.mainGroupContainer}
        >
          {memoryGameMainGroupProps && (
            <MemoryGameMainGroup {...memoryGameMainGroupProps} />
          )}
          {handwritingGameMainGroupProps && (
            <HandwritingGameMainGroup {...handwritingGameMainGroupProps} />
          )}
        </div>
        <div className={styles.adsContainer}>
          <Skeleton
            height={'100%'}
            width={'100%'}
            style={{ boxShadow: '0 0 0.25rem #1D656B80' }}
          />
        </div>

        <AnimatePresence>
          {isModalOpen && gameHudProps.instruction && (
            <motion.div
              className={styles.modalBackdrop}
              onClick={closeModal}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className={styles.modalContent}
                onClick={(e) => e.stopPropagation()}
                variants={{
                  open: { opacity: 1, scaleX: 1, scaleY: 1 },
                  closed: { opacity: 0, scaleX: 0.8, scaleY: 0 },
                }}
                initial="closed"
                animate="open"
                exit="closed"
                transition={{ duration: 0.3 }}
              >
                <TTSQuestionPanel
                  instruction={gameHudProps.instruction}
                  audioSrc={audioSrc}
                  onPressClose={closeModal}
                  onClickPlayAudio={() => {
                    TTsPlayerRef.current?.stop();
                    gameController.stopAudio();
                  }}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default GameplayTemplate;
